"use client"

import { useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { Sun, Moon, User, SignOut, CaretRight, CaretLeft } from "phosphor-react"
import { motion, AnimatePresence } from "framer-motion"
import { useTheme } from "../../utils/context/ThemeContext"
import { useCurrentUser } from "../../utils/context/UserProvider"
import SignOutModal from "./sign-out-modal"
import NotificationDropdown from "./NotificationDropdown"
import { useClerk } from "@clerk/clerk-react"

const Navbar = ({ collapsed, setCollapsed }) => {
  const { t } = useTranslation()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [showSignOutModal, setShowSignOutModal] = useState(false)
  const dropdownRef = useRef(null)
  const { theme, toggleTheme } = useTheme()
  const { currentUser } = useCurrentUser()
  const location = useLocation()
  const { signOut } = useClerk()
  const navigate = useNavigate()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const handleLogOut = async () => {
    try {
      await signOut() // Clerk sign-out
      navigate("/login")
    } finally {
      setShowSignOutModal(false)
      navigate("/login")
    }
  }

  // Animation variants
  const dropdownVariants = {
    hidden: {
      opacity: 0,
      y: -5,
      scale: 0.95,
      transition: {
        duration: 0.2,
        ease: "easeInOut",
      },
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.2,
        ease: "easeOut",
      },
    },
  }

  const iconButtonVariants = {
    hover: {
      scale: 1.05,
      transition: { duration: 0.2 },
    },
    tap: {
      scale: 0.95,
      transition: { duration: 0.1 },
    },
  }

  // Simple route mapping
  const getRouteName = (pathname) => {
    const routes = {
      "/": "Dashboard",
      "/settings/roles": "Roles Management",
      "/settings/roles/New": "Create New Role",
      "/settings/users": "Users Management",
      "/settings/account": "Account Settings",
      "/settings/payments-info": "Payment Information",
      "/settings/providers": "Providers",
      "/ad-accounts": "Accounts List",
      "/expenses": "Assets",
      "/tickets": "Support Tickets",
      "/credit-accounts": "Balance Tracking",
      "/credit-accounts-history": "Balance Records",
      "/leads": "Leads",
      "/financial-stats": "Statistics",
      "/top-ups": "Financial Report",
      "/clients": "Clients",
      "/social-login": "Social Login",
      "/login": "Login",
      "/add-balance-credit": "Add Balance & Credit",
    }

    if (pathname.includes("/settings/roles/update/")) {
      return "Update Role"
    }
    if (pathname.includes("/ad-accounts/details/")) {
      return "Ad Account Details"
    }

    return routes[pathname] || "Page"
  }

  const currentRouteName = getRouteName(location.pathname)

  return (
    <>
      <motion.div
        className={`h-16 ${
          !collapsed ? "lg:pl-60" : "lg:pl-[5.5rem]"
        } bg-white  dark:bg-neutral-950 text-neutral-800 dark:text-white border-b border-neutral-150 dark:border-neutral-900 fixed top-0 right-0 left-0 z-10 transition-all duration-300`}
        initial={false}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        <div className="flex items-center justify-between h-full px-4 mx-auto w-full">
          {/* Left side - Title and collapse button */}
          <div className="flex items-center space-x-3">
            <motion.button
              onClick={() => setCollapsed(!collapsed)}
              className="p-1.5 rounded-full text-neutral-800 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-all duration-200"
              aria-label={collapsed ? t("Expand sidebar") : t("Collapse sidebar")}
              variants={iconButtonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              {collapsed ? <CaretRight size={18} /> : <CaretLeft size={18} />}
            </motion.button>
            <motion.div
              className="flex items-center"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h1 className="lg:text-lg font-[400]">{currentRouteName}</h1>
            </motion.div>
          </div>

          {/* Right side - Icons and User */}
          <div className="flex items-center space-x-1 px-3">
            <NotificationDropdown />

            <motion.button
              onClick={toggleTheme}
              className="p-2 rounded-full text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
              aria-label={
                theme === "dark"
                  ? t("Switch to light mode")
                  : t("Switch to dark mode")
              }
              variants={iconButtonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </motion.button>

            {/* User dropdown */}
            <div className="relative" ref={dropdownRef}>
              <motion.button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="p-2 rounded-full text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                variants={iconButtonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <User size={18} />
              </motion.button>

              <AnimatePresence>
                {dropdownOpen && (
                  <motion.div
                    className="absolute right-0 mt-2 w-56 bg-white dark:bg-neutral-900/80 backdrop-blur-md border border-neutral-150 dark:border-neutral-900 text-neutral-800 dark:text-white rounded-xl shadow-lg  z-50 overflow-hidden"
                    variants={dropdownVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    <div className="m-3 p-3 bg-neutral-50 rounded-xl dark:bg-neutral-800">
                      <p className="text-sm font-medium">
                        {currentUser?.fullName}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {currentUser?.email}
                      </p>
                    </div>

                    <Link
                      to="/settings/account"
                      className="block px-4 py-2.5 text-sm mx-2 rounded-xl text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <motion.div
                        className="flex items-center"
                        whileHover={{ x: 2 }}
                        transition={{ duration: 0.2 }}
                      >
                        <User
                          size={16}
                          className="mr-3 text-neutral-500 dark:text-neutral-400"
                        />
                        {t("Account Details")}
                      </motion.div>
                    </Link>

                    <Link
                      onClick={() => {
                        setDropdownOpen(false)
                        setShowSignOutModal(true)
                      }}
                      className="block px-4 py-2.5  text-sm mx-2 mb-2 rounded-xl text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                      whileHover={{ x: 2 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="flex items-center">
                        <SignOut
                          size={16}
                          className="mr-3 text-neutral-500 dark:text-neutral-400"
                        />
                        {t("Sign Out")}
                      </div>
                    </Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Sign Out Confirmation Modal */}
      <SignOutModal
        isOpen={showSignOutModal}
        onClose={() => setShowSignOutModal(false)}
        onConfirm={handleLogOut}
      />
    </>
  )
}

export default Navbar
